import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../../app/locationSlice";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../../assets/images/shersoftnavLogo.png";

// ─── Tab Icon SVGs ─────────────────────────────────────────────────────────────
const IconProfile = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);
const IconContact = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IconPayroll = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const IconPayrollBadge = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
  </svg>
);

// ─── Field helpers ─────────────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <label className="text-[12px] font-bold text-gray-700 select-none font-Inter">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const inputCls =
  "w-full h-[36px] px-3 text-xs border border-[#cbd5e1] rounded-[6px] outline-none focus:border-[#0008a9] box-border bg-white font-Inter";

// ═══════════════════════════════════════════════════════════════════════════════
function StaffRegMob({ onClose }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);
  const activeLocation = useSelector(selectActiveLocation);
  const navigate = useNavigate();

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("profile");
  const [dataFound, setDataFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    sr_empid: "",
    sr_Name: "",
    sr_Section: "",
    sr_cashAcc: "",
    sr_Location: activeLocation ? activeLocation.id : "",
    sr_Gender: "Male",
    sr_Address1: "",
    sr_Address2: "",
    sr_Address3: "",
    sr_Mobile: "",
    sr_Type: "EMPLOYEE",
    sr_DateOj: "",
    sr_Salary: "",
    sr_OtRate: "",
    sr_OtHour: "",
    sr_LeaveDeduction: "",
    sr_DailyAllowance: "",
    sr_Active: 1,
    sr_CasualLeave: "",
    sr_Branch: "",
    sr_CommissionStatus: "",
    sr_Percent: "",
    sr_Pf: "",
    sr_WorkingHours: "",
    sr_UserName: "",
    sr_Password: "",
  });

  // ── Option lists ──────────────────────────────────────────────────────────────
  const [employeeNames, setEmployeeNames] = useState([]);
  const [employeeNamesList, setEmployeeNamesList] = useState([]);
  const [selectcashAcc, setselectcashAcc] = useState([]);
  const [locationOpts, setLocationOpts] = useState([]);

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const nameInputRef        = useRef(null);
  const sectionInputRef     = useRef(null);
  const cashAccInputRef     = useRef(null);
  const locationInputRef    = useRef(null);
  const genderMaleRef       = useRef(null);
  const address1InputRef    = useRef(null);
  const address2InputRef    = useRef(null);
  const address3InputRef    = useRef(null);
  const mobileInputRef      = useRef(null);
  const percentInputRef     = useRef(null);
  const saveBtnRef          = useRef(null);
  const editBtnRef          = useRef(null);

  // ─── Field-order for Enter navigation (mirrors desktop tab order) ──────────
  // Profile → Contact → Payroll (Commission % → Active checkbox → Save/Edit)
  const fieldOrder = [
    { id: "sr_Name",     ref: nameInputRef,    tab: "profile" },
    { id: "sr_Section",  ref: sectionInputRef, tab: "profile" },
    { id: "sr_cashAcc",  ref: cashAccInputRef,  tab: "profile" },
    { id: "sr_Location", ref: locationInputRef, tab: "profile" },
    { id: "sr_Gender",   ref: genderMaleRef,    tab: "profile" },
    { id: "sr_Address1", ref: address1InputRef, tab: "contact" },
    { id: "sr_Address2", ref: address2InputRef, tab: "contact" },
    { id: "sr_Address3", ref: address3InputRef, tab: "contact" },
    { id: "sr_Mobile",   ref: mobileInputRef,   tab: "contact" },
    // Payroll tab — mirrors desktop "default" tab exactly
    { id: "sr_Percent",  ref: percentInputRef,  tab: "payroll" },
  ];

  // Focus a field by ID — auto-switches tab and retries if element not yet mounted
  const focusFieldById = (id, attempt = 0) => {
    const entry = fieldOrder.find((f) => f.id === id);
    if (!entry) return;

    if (entry.tab && entry.tab !== activeTab) {
      setActiveTab(entry.tab);
    }

    requestAnimationFrame(() => {
      const el = entry.ref.current;
      if (el && typeof el.focus === "function") {
        el.focus();
        if (el.select) el.select();
      } else if (attempt < 15) {
        setTimeout(() => focusFieldById(id, attempt + 1), 25);
      }
    });
  };

  // Enter-key navigation
  const handleEnterNav = (event, currentId) => {
    if (event.key !== "Enter" && event.key !== "Return") return;
    event.preventDefault();

    const idx = fieldOrder.findIndex((f) => f.id === currentId);
    if (idx === -1) return;

    const next = fieldOrder[idx + 1];
    if (next) {
      focusFieldById(next.id);
    } else {
      // last field → focus save or edit
      if (!dataFound && saveBtnRef.current) saveBtnRef.current.focus();
      else if (dataFound && editBtnRef.current) editBtnRef.current.focus();
    }
  };

  // ─── Data fetchers ─────────────────────────────────────────────────────────
  const fetchEmployeeNames = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/main/getEmployeeNames/${dbCode}`);
      const opts = res.data.map((item) => ({ value: item.Auto, label: item.name }));
      setEmployeeNames(opts);
      setEmployeeNamesList(res.data.map((i) => i.name));
    } catch (e) {
      console.error("fetchEmployeeNames:", e.message);
    }
  };

  const fetchEmployeeId = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/main/getEmployeeId/${dbCode}`);
      setFormData((prev) => ({ ...prev, sr_empid: res.data[0]?.id || "" }));
    } catch (e) {
      console.error("fetchEmployeeId:", e.message);
    }
  };

  const fetchCashAccounts = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/main/SchRec_caba/${dbCode}`);
      setselectcashAcc(res.data.map((item) => ({ value: item.ledcode, label: item.LedName })));
    } catch (e) {
      console.error("fetchCashAccounts:", e.message);
    }
  };

  const fetchLocationOpts = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
      if (Array.isArray(res.data)) {
        setLocationOpts(
          res.data
            .filter((r) => String(r.Type || r.type || "").toLowerCase() === "location")
            .map((r) => ({
              label: r.Name || r.name || "",
              value: r.Auto || r.auto || r.SlNo || r.slno || "",
            }))
        );
      }
    } catch (e) {
      console.error("fetchLocationOpts:", e.message);
    }
  };

  useEffect(() => {
    if (dbCode) {
      fetchEmployeeNames();
      fetchEmployeeId();
      fetchCashAccounts();
      fetchLocationOpts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbCode]);

  // Focus first field on mount
  useEffect(() => {
    setTimeout(() => nameInputRef.current?.focus?.(), 200);
  }, []);

  // ─── Actions ───────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toISOString().split("T")[0];
  };

  const handleClear = () => {
    fetchEmployeeId();
    fetchEmployeeNames();
    setFormData({
      sr_empid: "",
      sr_Name: "",
      sr_Section: "",
      sr_cashAcc: "",
      sr_Location: activeLocation ? activeLocation.id : "",
      sr_Gender: "Male",
      sr_Address1: "",
      sr_Address2: "",
      sr_Address3: "",
      sr_Mobile: "",
      sr_Type: "EMPLOYEE",
      sr_DateOj: "",
      sr_Salary: "",
      sr_OtRate: "",
      sr_OtHour: "",
      sr_LeaveDeduction: "",
      sr_DailyAllowance: "",
      sr_Active: 1,
      sr_CasualLeave: "",
      sr_Branch: "",
      sr_CommissionStatus: "",
      sr_Percent: "",
      sr_Pf: "",
      sr_WorkingHours: "",
      sr_UserName: "",
      sr_Password: "",
    });
    setDataFound(false);
    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
    setActiveTab("profile");
    setTimeout(() => nameInputRef.current?.focus?.(), 100);
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    const nameStr = formData.sr_Name?.name ?? String(formData.sr_Name ?? "");
    if (!nameStr || !formData.sr_Section) {
      alert("Please enter both Employee Name and Section.");
      return;
    }
    if (employeeNamesList.includes(nameStr.trim())) {
      alert("Duplicate entry! Employee name already exists.");
      return;
    }
    if (formData.sr_Mobile && String(formData.sr_Mobile).length !== 10) {
      alert("Mobile number must be exactly 10 digits.");
      return;
    }
    if (!window.confirm(`Save employee "${nameStr}"?`)) return;

    setIsLoading(true);
    try {
      const payload = { ...formData, sr_Name: nameStr };
      const res = await axios.post(`${apiBaseUrl}/main/insertSalesman/${dbCode}`, payload);
      if (res.status === 200) {
        alert("Employee Registered Successfully!");
        handleClear();
      }
    } catch (e) {
      console.error("handleSubmit:", e.message);
      alert("Error saving employee.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFind = async () => {
    let autoId = formData.sr_Name?.id;
    if (!autoId) {
      const typed = String(formData.sr_Name ?? "").trim().toLowerCase();
      if (!typed) { alert("Select or type a staff name to find."); return; }
      const match = employeeNames.find((o) => o.label.trim().toLowerCase() === typed);
      if (!match) { alert("Employee not found. Please select from the dropdown."); return; }
      autoId = match.value;
    }
    try {
      const res = await axios.get(`${apiBaseUrl}/main/getEmployeeByName/${autoId}/${dbCode}`);
      const d = res.data[0];
      if (d) {
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        setFormData({
          ...formData,
          sr_empid: d.emp_code || "",
          sr_Name: d.Name ? { id: d.emp_code, name: d.Name } : "",
          sr_Section: d.EmployeeSection || "",
          sr_cashAcc: d.cashAcc || "",
          sr_Location: d.Location || "",
          sr_Gender: d.Gender || "Male",
          sr_Address1: d.Address1 || "",
          sr_Address2: d.address2 || "",
          sr_Address3: d.address3 || "",
          sr_Mobile: d.Mobile || "",
          sr_Type: d.Type || "EMPLOYEE",
          sr_DateOj: formatDate(d.DDate),
          sr_Salary: d.Salary || "",
          sr_OtRate: d.OT || "",
          sr_OtHour: d.othour || "",
          sr_LeaveDeduction: d.LiveDeduction || "",
          sr_DailyAllowance: d.DAllowance || "",
          sr_Active: d.Active !== undefined ? d.Active : 1,
          sr_CasualLeave: d.CashualLeave || "",
          sr_CommissionStatus: d.salescomStatus || "",
          sr_Percent: d.commisionper || "",
          sr_Pf: d.pf || "",
          sr_WorkingHours: d.WorkingHour || "",
          sr_UserName: d.userName || "",
          sr_Password: d.password || "",
        });
        setDataFound(true);
      } else {
        alert("Employee record not found.");
      }
    } catch (e) {
      console.error("handleFind:", e.message);
    }
  };

  const handleEdit = async () => {
    if (isLoading) return;
    const nameStr = formData.sr_Name?.name ?? String(formData.sr_Name ?? "");
    if (!nameStr || !formData.sr_Section) { alert("Enter Data Correctly"); return; }
    if (formData.sr_Mobile && String(formData.sr_Mobile).length !== 10) {
      alert("Invalid Mobile Number.");
      return;
    }
    if (!window.confirm(`Update employee details for "${nameStr}"?`)) return;
    setIsLoading(true);
    try {
      const payload = { ...formData, sr_Name: nameStr };
      const res = await axios.post(`${apiBaseUrl}/main/updateSalesman/${dbCode}`, payload);
      if (res.status === 200) { alert("Employee Details Updated!"); handleClear(); }
    } catch (e) {
      console.error("handleEdit:", e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isLoading) return;
    const nameStr = formData.sr_Name?.name ?? String(formData.sr_Name ?? "");
    if (!nameStr) return;
    if (!window.confirm(`Are you sure you want to delete "${nameStr}"?`)) return;
    setIsLoading(true);
    try {
      const res = await axios.delete(
        `${apiBaseUrl}/main/deleteSalesman/${encodeURIComponent(nameStr)}/${dbCode}?locationId=${activeLocation ? activeLocation.id : ''}`
      );
      if (res.status === 200) {
        alert(res.data.message || "Employee Deleted Successfully!");
        handleClear();
      }
    } catch (e) {
      console.error("handleDelete:", e.message);
    }
  };

  const updateNavRefs = (refs) => {
    if (refs.saveBtnRef)    saveBtnRef.current    = refs.saveBtnRef.current;
    if (refs.saveButtonRef) saveBtnRef.current    = refs.saveButtonRef.current;
    if (refs.editButtonRef) editBtnRef.current    = refs.editButtonRef.current;
  };

  // ─── Tab config (Profile | Contact | Payroll) ─────────────────────────────
  const TABS = [
    { key: "profile", label: "Profile",  Icon: IconProfile },
    { key: "contact", label: "Contact",  Icon: IconContact },
    { key: "payroll", label: "Payroll",  Icon: IconPayroll },
  ];

  const comboCls = "w-full h-[36px] outline-none";
  const comboInputCls = "rounded-[6px] border border-[#cbd5e1]";
  const comboInputStyle = { fontSize: "12px", paddingLeft: "8px" };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 w-full h-full bg-[#f0f0f0] flex flex-col pointer-events-auto z-[10000] overflow-y-auto font-Inter">

      {/* Window Header */}
      <WindowHeader
        title="Staff / Employee Registration"
        logo={headeLogo}
        onClose={onClose || (() => navigate("/home"))}
        className="bg-[rgb(0,8,169)] rounded-none"
        closeBtnClassName="w-6 h-6 border-none rounded-none mr-2"
      />

      {/* Action Toolbar */}
      <div className="w-full flex-shrink-0">
        <ActionToolbar
          width="100%"
          height="auto"
          bgColor="#EAF0FC"
          showSave={true}
          showPrint={false}
          showClear={true}
          showFind={true}
          showEdit={true}
          showDelete={true}
          showExit={true}
          showNavigation={false}
          saveDisabled={dataFound || isLoading || saveDisabled}
          editDisabled={!dataFound || editDisabled || isLoading}
          deleteDisabled={!dataFound || deleteDisabled || isLoading}
          dataFound={dataFound}
          permissions={permissions}
          permissionKey="employeeReg"
          onSave={handleSubmit}
          onClear={handleClear}
          onFind={handleFind}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExit={onClose || (() => navigate("/home"))}
          onRefsUpdate={updateNavRefs}
        />
      </div>

      {/* Tab Navigation */}
      <div className="w-full flex-shrink-0 border-b border-[#b8cce0] bg-white">
        <div className="flex justify-between px-1 pt-2 overflow-x-auto gap-1">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-3 py-2 text-[11px] font-bold font-Inter border-none cursor-pointer transition-all duration-150 flex-1 text-center whitespace-nowrap ${
                activeTab === key
                  ? "bg-[#E8EEF8] text-[#0008a9] rounded-t-lg border-b-4 border-[#0008a9]"
                  : "bg-transparent text-[#1a2e4a] hover:bg-[#1a2e4a14]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Body */}
      <div className="flex-1 p-4 bg-[#f8fafc] overflow-y-auto pb-24">

        {/* ══ PROFILE TAB ══════════════════════════════════════════════════════ */}
        {activeTab === "profile" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            {/* Section header */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <IconProfile />
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0 font-Inter">Staff Profile</h2>
                <span className="text-[11px] text-gray-500 font-Inter">Fill in the employee identification details below</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {/* Employee ID – read-only */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Employee ID</FieldLabel>
                <input
                  type="text"
                  readOnly
                  value={formData.sr_empid}
                  className={`${inputCls} bg-slate-50 text-center font-semibold font-Inter text-[#0008a9] cursor-default`}
                  placeholder="Auto-generated"
                />
              </div>

              {/* Staff Name */}
              <div className="flex flex-col gap-1">
                <FieldLabel required>Staff Name</FieldLabel>
                <SherComboBox
                  comboRef={nameInputRef}
                  options={employeeNames}
                  value={formData.sr_Name?.name ?? formData.sr_Name}
                  onSelect={(opt) =>
                    setFormData({ ...formData, sr_Name: { id: opt.value, name: opt.label } })
                  }
                  onFreeInput={(text) => setFormData({ ...formData, sr_Name: text })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Name")}
                  placeholder="Select or type employee name..."
                  showArrow={true}
                  clearable={false}
                  className={comboCls}
                  inputClassName={comboInputCls}
                  inputStyle={comboInputStyle}
                />
              </div>

              {/* Section */}
              <div className="flex flex-col gap-1">
                <FieldLabel required>Section / Department</FieldLabel>
                <input
                  type="text"
                  ref={sectionInputRef}
                  value={formData.sr_Section}
                  onChange={(e) => setFormData({ ...formData, sr_Section: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Section")}
                  placeholder="e.g. Sales Counter"
                  className={inputCls}
                />
              </div>

              {/* Cash Account */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Cash Account</FieldLabel>
                <SherComboBox
                  comboRef={cashAccInputRef}
                  options={selectcashAcc}
                  value={formData.sr_cashAcc}
                  onSelect={(opt) => setFormData({ ...formData, sr_cashAcc: opt.value })}
                  onFreeInput={(text) => setFormData({ ...formData, sr_cashAcc: text })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_cashAcc")}
                  placeholder="Select cash account..."
                  showArrow={true}
                  clearable={false}
                  className={comboCls}
                  inputClassName={comboInputCls}
                  inputStyle={comboInputStyle}
                />
              </div>

              {/* Location */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Location</FieldLabel>
                <SherComboBox
                  comboRef={locationInputRef}
                  options={locationOpts}
                  value={formData.sr_Location}
                  onSelect={(opt) => setFormData({ ...formData, sr_Location: opt.value })}
                  onFreeInput={(text) => setFormData({ ...formData, sr_Location: text })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Location")}
                  placeholder="Select location..."
                  showArrow={true}
                  clearable={false}
                  className={comboCls}
                  inputClassName={comboInputCls}
                  inputStyle={comboInputStyle}
                />
              </div>

              {/* Gender */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Gender</FieldLabel>
                <div className="flex gap-5 items-center h-[36px] px-2 border border-[#cbd5e1] rounded-[6px] bg-white">
                  {["Male", "Female"].map((g) => (
                    <label key={g} className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold font-Inter text-gray-700 select-none">
                      <input
                        ref={g === "Male" ? genderMaleRef : undefined}
                        type="radio"
                        name="sr_gender_mob"
                        value={g}
                        checked={formData.sr_Gender === g}
                        onChange={() => setFormData({ ...formData, sr_Gender: g })}
                        onKeyDown={(e) => handleEnterNav(e, "sr_Gender")}
                        className="w-3.5 h-3.5 accent-[#0008a9]"
                      />
                      {g}
                    </label>
                  ))}
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="checkbox"
                  id="mob_sr_active"
                  checked={formData.sr_Active === 1}
                  onChange={(e) =>
                    setFormData({ ...formData, sr_Active: e.target.checked ? 1 : 0 })
                  }
                  className="w-4 h-4 accent-[#0008a9] cursor-pointer"
                />
                <label htmlFor="mob_sr_active" className="text-xs font-bold font-Inter text-gray-700 cursor-pointer select-none">
                  Active — mark to allow sales attribution
                </label>
              </div>
            </div>

            {/* Info banner */}
            <div className="mt-2 bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-3 flex gap-2.5 items-start">
              <div className="text-[#4338CA] shrink-0 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>
              <p className="text-[11px] font-semibold font-Inter text-[#4338CA] m-0 leading-snug">
                Staff registration creates salesman profiles automatically linked with daily sales &amp; billing commissions.
              </p>
            </div>
          </div>
        )}

        {/* ══ CONTACT TAB ══════════════════════════════════════════════════════ */}
        {activeTab === "contact" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <IconContact />
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0 font-Inter">Contact Details</h2>
                <span className="text-[11px] text-gray-500 font-Inter">Address and mobile contact information</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {/* Address 1 */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Address Line 1</FieldLabel>
                <input
                  type="text"
                  ref={address1InputRef}
                  value={formData.sr_Address1}
                  onChange={(e) => setFormData({ ...formData, sr_Address1: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Address1")}
                  placeholder="Street / Building"
                  className={inputCls}
                />
              </div>

              {/* Address 2 */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Address Line 2</FieldLabel>
                <input
                  type="text"
                  ref={address2InputRef}
                  value={formData.sr_Address2}
                  onChange={(e) => setFormData({ ...formData, sr_Address2: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Address2")}
                  placeholder="Area / Locality"
                  className={inputCls}
                />
              </div>

              {/* Address 3 */}
              <div className="flex flex-col gap-1">
                <FieldLabel>City / State</FieldLabel>
                <input
                  type="text"
                  ref={address3InputRef}
                  value={formData.sr_Address3}
                  onChange={(e) => setFormData({ ...formData, sr_Address3: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Address3")}
                  placeholder="City, State"
                  className={inputCls}
                />
              </div>

              {/* Mobile */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Mobile Number</FieldLabel>
                <div className="relative flex items-center w-full">
                  <input
                    type="number"
                    ref={mobileInputRef}
                    value={formData.sr_Mobile}
                    onChange={(e) => setFormData({ ...formData, sr_Mobile: e.target.value })}
                    onKeyDown={(e) => handleEnterNav(e, "sr_Mobile")}
                    placeholder="10-digit mobile number"
                    className={`${inputCls} pr-10`}
                  />
                  {/* Search by mobile */}
                  <button
                    type="button"
                    title="Find by mobile"
                    onClick={async () => {
                      if (!formData.sr_Mobile) return;
                      // reuse handleFind if name matches mobile — show info
                      alert("Use the Find button to search by name.");
                    }}
                    className="absolute right-0 top-0 h-full w-[38px] flex items-center justify-center bg-[#f1f5f9] border-l border-[#cbd5e1] rounded-r-[6px] text-[#0008a9] hover:bg-slate-200 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </button>
                </div>
                {formData.sr_Mobile && String(formData.sr_Mobile).length !== 10 && (
                  <span className="text-[10px] text-red-500 font-semibold font-Inter">Must be 10 digits</span>
                )}
              </div>
            </div>
          </div>
        )}


        {/* ══ PAYROLL TAB — mirrors desktop StaffReg "default" tab exactly ═══ */}
        {activeTab === "payroll" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <IconPayrollBadge />
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0 font-Inter">Payroll</h2>
                <span className="text-[11px] text-gray-500 font-Inter">Commission and active status</span>
              </div>
            </div>

            <div className="flex flex-col gap-4 mt-1">
              {/* Commission % — same as desktop */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Commission %</FieldLabel>
                <input
                  type="number"
                  ref={percentInputRef}
                  value={formData.sr_Percent}
                  onChange={(e) => setFormData({ ...formData, sr_Percent: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "sr_Percent")}
                  placeholder="e.g. 2.5"
                  className={inputCls}
                />
              </div>

              {/* Active Status — same as desktop */}
              <div className="flex items-center gap-3 p-3 bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg">
                <input
                  type="checkbox"
                  id="mob_payroll_active"
                  checked={formData.sr_Active === 1}
                  onChange={(e) =>
                    setFormData({ ...formData, sr_Active: e.target.checked ? 1 : 0 })
                  }
                  className="w-4 h-4 accent-[#0008a9] cursor-pointer"
                />
                <label
                  htmlFor="mob_payroll_active"
                  className="text-xs font-bold font-Inter text-[#4338CA] cursor-pointer select-none"
                >
                  Active — enable this staff for sales &amp; commission attribution
                </label>
              </div>
            </div>

            {/* Info banner */}
            <div className="mt-2 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-3 flex gap-2.5 items-start">
              <div className="text-[#D97706] shrink-0 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className="text-[11px] font-semibold font-Inter text-[#D97706] m-0 leading-snug">
                Commission % is applied on sales billing. Uncheck Active to deactivate this staff profile.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save / Edit floating button */}
      <div className="fixed bottom-0 left-0 right-0 px-4 py-3 bg-white border-t border-gray-200 shadow-lg z-50">
        {!dataFound ? (
          <button
            ref={saveBtnRef}
            onClick={handleSubmit}
            disabled={isLoading || saveDisabled}
            className="w-full h-[44px] bg-[#0008a9] hover:bg-[#0006c8] disabled:bg-[#a0aec0] text-white font-bold font-Inter text-sm rounded-[8px] transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Employee
              </>
            )}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              ref={editBtnRef}
              onClick={handleEdit}
              disabled={editDisabled || isLoading}
              className="flex-1 h-[44px] bg-[#0008a9] hover:bg-[#0006c8] disabled:bg-[#a0aec0] text-white font-bold font-Inter text-sm rounded-[8px] transition-colors flex items-center justify-center gap-2"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Update
            </button>
            <button
              onClick={handleClear}
              className="flex-1 h-[44px] bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-Inter text-sm rounded-[8px] transition-colors border border-slate-300 flex items-center justify-center gap-2"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-3.95" />
              </svg>
              Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffRegMob;
